import React, { useCallback, useEffect, useRef } from 'react';
import { ControllerGlobals, MODKEYS_STATE } from 'src/components/Planner/controller';
import { store } from 'src/app/store';
import { showSnackBar } from 'src/api/ui-slice';
import { removeGhostDevice, unHighlight } from 'src/components/Planner/planner/planner';
import { PlannerGlobals } from 'src/components/Planner/planner/planner-globals';
import {
    createDragPreview,
    refreshDragPreviewState,
    removeDragPreview,
    setGrabbingCursor,
    updateDragPreview
} from 'src/components/Planner/planner/dragPreview';
import { DraggableDevice } from 'src/pages/PlannerPage/DeviceView/hooks/DragAndDrop/draggableDevices';
import { ts } from 'src/utils/translate';
import { calcImageURL } from 'src/utils/url';

const DRAG_THRESHOLD = 5;

function isMultiPlaceableType(device: DraggableDevice | null): boolean {
    return !!device && (device.type === 'device' || device.type === 'custom_device');
}

let armedPreview: HTMLImageElement | null = null;
let armedPreviewFollowHandler: ((e: MouseEvent) => void) | null = null;
let armedShiftReleaseHandler: ((e: KeyboardEvent) => void) | null = null;

export function endMultiPlacement() {
    ControllerGlobals.currentlyDraggedDevice = null;
    setGrabbingCursor(false);
    unHighlight();
    unHighlight('all');
    removeGhostDevice();
    removeDragPreview(armedPreview);
    armedPreview = null;
    if (armedPreviewFollowHandler) {
        document.removeEventListener('mousemove', armedPreviewFollowHandler);
        armedPreviewFollowHandler = null;
    }
    if (armedShiftReleaseHandler) {
        document.removeEventListener('keyup', armedShiftReleaseHandler);
        armedShiftReleaseHandler = null;
    }
}

function beginMultiPlacement(preview: HTMLImageElement | null) {
    if (preview) {
        armedPreview = preview;
        armedPreviewFollowHandler = (e: MouseEvent) => {
            if (armedPreview) {
                updateDragPreview(armedPreview, e.clientX, e.clientY);
                refreshDragPreviewState(armedPreview);
            }
        };
        document.addEventListener('mousemove', armedPreviewFollowHandler);
    }
    armedShiftReleaseHandler = (e: KeyboardEvent) => {
        if (!e.shiftKey) {
            endMultiPlacement();
        }
    };
    document.addEventListener('keyup', armedShiftReleaseHandler);
}

/**
 * Hook that bridges React drag events to the planner's ControllerGlobals.currentlyDraggedDevice.
 *
 * On mousedown, starts tracking. After moving > 5px, sets the global so the planner's
 * existing mouseup handler and ghost preview work automatically.
 *
 * When Shift is held on drop, the device stays armed so it can be placed repeatedly by clicking;
 * the cursor preview keeps following the mouse until Shift is released (see beginMultiPlacement).
 */
export function usePlannerDrag(device: DraggableDevice) {
    const deviceRef = useRef(device);
    deviceRef.current = device;

    const isMouseDraggingRef = useRef(false);
    const mouseStartPositionRef = useRef({ x: 0, y: 0 });
    const isDraggingThresholdExceededRef = useRef(false);
    const wasDraggedRef = useRef(false);
    const dragPreviewRef = useRef<HTMLImageElement | null>(null);

    const cleanup = useCallback(
        (mouseMoveHandler: (e: MouseEvent) => void, mouseUpHandler: () => void) => {
            isMouseDraggingRef.current = false;
            mouseStartPositionRef.current = { x: 0, y: 0 };
            isDraggingThresholdExceededRef.current = false;
            setGrabbingCursor(false);
            removeDragPreview(dragPreviewRef.current);
            dragPreviewRef.current = null;
            document.removeEventListener('mousemove', mouseMoveHandler);
            document.removeEventListener('mouseup', mouseUpHandler);
        },
        []
    );

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isMouseDraggingRef.current) return;

        const dx: number = e.clientX - mouseStartPositionRef.current.x;
        const dy: number = e.clientY - mouseStartPositionRef.current.y;
        const distance: number = Math.sqrt(dx * dx + dy * dy);

        if (!isDraggingThresholdExceededRef.current && distance > DRAG_THRESHOLD) {
            isDraggingThresholdExceededRef.current = true;
            wasDraggedRef.current = true;
            ControllerGlobals.currentlyDraggedDevice = deviceRef.current;
            // Reset the planner's mouse-down anchor so the first drop isn't treated
            // as a plan pan (which would otherwise block placing while Shift is held).
            PlannerGlobals.mouseDownPageXY = undefined;
            setGrabbingCursor(true);
            const icon: string = calcImageURL(deviceRef.current.icon ?? deviceRef.current.image);
            if (icon) {
                dragPreviewRef.current = createDragPreview(icon);
            }
            if (!MODKEYS_STATE.shift && isMultiPlaceableType(deviceRef.current)) {
                store.dispatch(showSnackBar({
                    text: ts('p_shift_devdrag',
                        'If you hold Shift while placing this, you can place this device multiple times.'),
                    severity: 'info',
                }));
            }
        }

        if (dragPreviewRef.current) {
            updateDragPreview(dragPreviewRef.current, e.clientX, e.clientY);
            refreshDragPreviewState(dragPreviewRef.current);
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        if (isDraggingThresholdExceededRef.current
            && MODKEYS_STATE.shift
            && isMultiPlaceableType(ControllerGlobals.currentlyDraggedDevice)) {
            isMouseDraggingRef.current = false;
            isDraggingThresholdExceededRef.current = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            beginMultiPlacement(dragPreviewRef.current);
            dragPreviewRef.current = null;
            return;
        }

        if (isDraggingThresholdExceededRef.current) {
            ControllerGlobals.currentlyDraggedDevice = null;
        }
        cleanup(handleMouseMove, handleMouseUp);
    }, [cleanup, handleMouseMove]);

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        const isLeftMouseButton = e.button === 0;
        if (!isLeftMouseButton) return;

        e.preventDefault();
        endMultiPlacement();
        isMouseDraggingRef.current = true;
        isDraggingThresholdExceededRef.current = false;
        wasDraggedRef.current = false;
        mouseStartPositionRef.current = { x: e.clientX, y: e.clientY };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove, handleMouseUp]);

    useEffect(() => {
        return () => cleanup(handleMouseMove, handleMouseUp);
    }, [cleanup, handleMouseMove, handleMouseUp]);

    return { onMouseDown, wasDraggedRef };
}
